import {
	Accordion,
	ManufacturerIcon,
	ToggleGroup,
	Tooltip,
} from "@au-schein-de/au-ui-library";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterHeader } from "../filter-header.component";
import { useFiltersContext } from "../filters.context";
import type { FiltersConfig } from "../types";
import { OtherProducersToggle } from "./other-producers-toggle.component";
import { ProducerFilterSearch } from "./producer-filter-search.component";

type ProducersConfig = Pick<FiltersConfig, "producers">;

const sortFn = (a: { name: string }, b: { name: string }) =>
	a.name.localeCompare(b.name);
const mapProducers = (producers: ProducersConfig) => ({
	topProducers: producers.producers.filter((p) => p.isTopProducer).sort(sortFn),
	otherProducers: producers.producers
		.filter((p) => !p.isTopProducer)
		.sort(sortFn),
});

interface ProducersFilterProps {
	children?: ReactNode;
}

export function ProducersFilter({ children }: ProducersFilterProps) {
	const { t } = useTranslation("product");
	const { filters, config, onFilterChange, onTopProducerWarning } =
		useFiltersContext();
	const value = filters.producers ?? [];

	const { topProducers, otherProducers } = mapProducers(config);

	// Controls visibility of the list of other (non-top) producers
	const [showOther, setShowOther] = useState(false);
	const [manuallyHidden, setManuallyHidden] = useState(false);

	// Search term for filtering producers by name
	const [searchTerm, setSearchTerm] = useState("");
	const normalizedSearch = searchTerm.trim().toLowerCase();

	// Reset manual hide when search changes
	useEffect(() => {
		if (normalizedSearch) {
			setManuallyHidden(false);
		}
	}, [normalizedSearch]);

	// Keep UI consistent with "Top producers" mode (e.g. snackbar Undo action).
	useEffect(() => {
		if (filters.topProducersLowPrice) {
			setShowOther(false);
			setManuallyHidden(true);
		}
	}, [filters.topProducersLowPrice]);

	const filteredTopProducers = normalizedSearch
		? topProducers.filter((p) =>
				p.name.toLowerCase().includes(normalizedSearch),
			)
		: topProducers;
	const filteredOtherProducers = normalizedSearch
		? otherProducers.filter((p) =>
				p.name.toLowerCase().includes(normalizedSearch),
			)
		: otherProducers;

	const finalShowOther =
		(!!normalizedSearch &&
			!!filteredOtherProducers.length &&
			!manuallyHidden) ||
		showOther;

	const toggleShowOther = () => {
		if (finalShowOther) {
			setShowOther(false);
			setManuallyHidden(true);
		} else {
			setShowOther(true);
			setManuallyHidden(false);
		}
	};

	const onValueChange = useCallback(
		(v: string[]) => {
			if (filters.topProducersLowPrice) {
				onTopProducerWarning?.();
			}
			onFilterChange(
				"producers",
				v.map((val) => Number(val)),
			);
			onFilterChange("topProducersLowPrice", false);
		},
		[filters.topProducersLowPrice, onFilterChange, onTopProducerWarning],
	);

	return (
		<Accordion.Item
			value="producers"
			variant="filter"
			data-testid="producers-filter"
		>
			<Accordion.Trigger
				variant="filter"
				data-testid="producers-filter-trigger"
			>
				<FilterHeader
					title={t("filters.producers")}
					value={Number(value?.length)}
					icon={<ManufacturerIcon size={5} />}
				/>
			</Accordion.Trigger>
			<Accordion.Content variant="filter">
				<ToggleGroup.Root
					type="multiple"
					variant="secondary"
					size="xs"
					className="flex-wrap justify-start gap-2"
					value={value.map((v: number) => v.toString())}
					onValueChange={onValueChange}
				>
					{/* Producer search */}
					<ProducerFilterSearch value={searchTerm} onChange={setSearchTerm} />

					{!!filteredTopProducers.length && (
						<div className="flex w-full items-center gap-1 text-body-sm">
							<span>{t("filters.topProducers")}</span>
							<Tooltip.Info
								info={t("filters.topProducersInfo")}
								iconClassName="text-gray-600"
								tooltipClassName="max-w-70 bg-white shadow-[0_4px_20px_0_#212A361A]"
								hasArrow={true}
								arrowClassName="fill-white"
							/>
						</div>
					)}

					{children}

					{filteredTopProducers.map((producer) => (
						<ToggleGroup.Item key={producer.id} value={producer.id.toString()}>
							{producer.name}
						</ToggleGroup.Item>
					))}

					{!!filteredOtherProducers.length && (
						<>
							<OtherProducersToggle
								expanded={finalShowOther}
								onToggle={toggleShowOther}
								label={
									finalShowOther
										? t("filters.hideOtherProducers")
										: t("filters.showOtherProducers")
								}
							/>
							{finalShowOther &&
								filteredOtherProducers.map((producer) => (
									<ToggleGroup.Item
										key={producer.id}
										value={producer.id.toString()}
									>
										{producer.name}
									</ToggleGroup.Item>
								))}
						</>
					)}

					{/* Empty state when search yields no matches */}
					{normalizedSearch &&
						!filteredTopProducers.length &&
						!filteredOtherProducers.length && (
							<div
								className="w-full py-2 text-left text-body-sm text-gray-500"
								data-testid="producer-filter-empty"
							>
								{t("filters.noProducersMatch")}
							</div>
						)}
				</ToggleGroup.Root>
			</Accordion.Content>
		</Accordion.Item>
	);
}
